import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import type { ChartDuration } from './chartDuration';

const durations: { value: ChartDuration; labelKey: string | null }[] = [
  { value: 'all', labelKey: 'all' },
  { value: '1y', labelKey: null },
  { value: '6m', labelKey: null },
  { value: '3m', labelKey: null },
];

export interface ChartDurationToggleProps {
  value: ChartDuration;
  onChange: (duration: ChartDuration) => void;
  className?: string;
}

/**
 * Timescale selector shared by the trend charts (lab results, care-plan measurements): a rolling
 * window of All / 1Y / 6M / 3M. Purely presentational — the caller owns the selection and applies
 * `getChartDurationCutoff` (see `chartDuration.ts`) to its data or chart domain.
 */
export const ChartDurationToggle = ({ value, onChange, className }: ChartDurationToggleProps): React.ReactNode => {
  const { t } = useTranslation('records');

  return (
    <ToggleGroup
      type="single"
      value={value}
      size="sm"
      className={cn('border border-border', className)}
      aria-label={t('chartTimescale')}
      onValueChange={(v) => {
        if (v) onChange(v as ChartDuration);
      }}
    >
      {durations.map((d) => (
        <ToggleGroupItem key={d.value} value={d.value} className="flex-1 text-xs">
          {d.labelKey ? t(d.labelKey) : d.value.toUpperCase()}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};
